import React, { Fragment } from "react";
import { Link } from "react-router-dom";
import { ActionType } from "../list/list/DataTable";
import { CashbackConfigRow, CashbackConfigUnifiedListRequest, CashbackConfigUnifiedListResponse, ListRequest, Source } from "../../http/protocol";
import { getContainer } from "../../ioc/IOCSetup";
import { EntityDataTable } from "../list/list/EntityDataTable";
import { withRouter } from "react-router";
import { ListView, ListViewProps, ListViewState } from "../ListView";
import { AdminEntityService } from "../../services/AdminEntityService";
import { CashbackConfigService } from "../../services/CashbackConfigService";
import { CashbackConfigV2Service } from "../../services/CashbackConfigV2Service";
import { ColumnType } from "../list/list/ColumnType";
import { t } from "../../i18n";
import { SourceBadge } from "./SourceBadge";
import { PayoutModeBanner } from "./PayoutModeBanner";
import { FeatureFlags } from "../../feature-flags/FeatureFlagContext";
import { withFeatureFlag } from "../../feature-flags/withFeatureFlag";

interface State extends ListViewState<CashbackConfigUnifiedListRequest> {
  warningFlag?: boolean;
  loadError?: boolean;
}

interface Props extends ListViewProps {
  cashbackV2Enabled: boolean;
}

export class CashbackConfigListView extends ListView<State, CashbackConfigUnifiedListRequest, Props> {
  columns = [
    {
      property: "source",
      name: "Source",
      type: ColumnType.COMPONENT,
      component: (item: CashbackConfigRow) => <SourceBadge source={item.source} />,
    },
    { property: "id", name: "Id", orderBy: "id" },
    { property: "name", name: "Name" },
    { property: "status", name: "Status" },
    { property: "type", name: "Type" },
    { property: "frequency", name: "Frequency" },
  ];

  actions = [
    {
      id: "edit",
      name: "Edit",
      className: "btn btn-primary",
      iconClassName: "fa fa-edit",
      type: ActionType.LINK,
      getLink: (item: CashbackConfigRow) => (item.source === Source.V2 ? "/cashback/v2/config/" + item.id : "/cashback/config/" + item.id),
    },
    { id: "delete", name: "Delete", className: "btn btn-danger", iconClassName: "fa fa-trash", type: ActionType.CONFIRMATION },
  ];

  configService: CashbackConfigService;
  configV2Service: CashbackConfigV2Service;

  // EntityDataTable fetches through service.list(); adapt it to the unified endpoint without touching the shared list framework.
  unifiedListService: AdminEntityService<CashbackConfigRow> = {
    list: (listRequest: ListRequest) => this.configService.listUnified(listRequest),
    save: () => Promise.reject(new Error("save is not supported on the unified cashback config list")),
    getEntity: () => Promise.reject(new Error("getEntity is not supported on the unified cashback config list")),
    remove: () => Promise.reject(new Error("remove is routed per source via onAction")),
  };

  constructor(props: Props) {
    super(props, "/cashback/configs/list");
    if (!this.state.listRequest?.orderBy) {
      this.state = { ...this.state, listRequest: { ...this.state.listRequest, orderBy: "id" } };
    }
    this.configService = getContainer().getCashbackConfigService();
    this.configV2Service = getContainer().getCashbackConfigV2Service();
  }

  onAction(item: CashbackConfigRow, action: string): Promise<any> | undefined {
    if (action === "delete" && item.id !== undefined && item.id !== null) {
      const removal = item.source === Source.V2 ? this.configV2Service.remove(String(item.id)) : this.configService.remove(String(item.id));
      return removal.then(() => this.refreshContent());
    }
  }

  getTitle() {
    return "List Cashback Configurations";
  }

  renderContent() {
    if (!this.state.listRequest) {
      return <div />;
    }
    const columns = this.props.cashbackV2Enabled ? this.columns : this.columns.filter((column) => column.property !== "source");
    return (
      <Fragment>
        <div className="wrapper wrapper-content">
          {this.props.cashbackV2Enabled && <PayoutModeBanner />}
          <div className="mb-3">
            <Link
              className="btn btn-primary"
              to="/cashback/config/create"
              data-testid="create-cashback-config"
            >
              {t("cashback.configs.create", undefined, "Create Cashback Config")}
            </Link>
          </div>
          {this.props.cashbackV2Enabled && this.state.warningFlag && (
            <div
              className="alert alert-warning mt-3"
              role="alert"
            >
              {t("cashback.configs.partial-warning", undefined, "Some cashback data could not be loaded. Results may be incomplete.")}
            </div>
          )}
          {this.state.loadError && (
            <div
              className="alert alert-danger mt-3"
              role="alert"
            >
              {t("cashback.configs.load-error", undefined, "Failed to load cashback configurations. Please try again later.")}
            </div>
          )}
          <EntityDataTable
            service={this.unifiedListService}
            onPagerChange={this.pagerChanged.bind(this)}
            columns={columns}
            listRequest={this.state.listRequest}
            onResults={(response: CashbackConfigUnifiedListResponse) => this.setState({ warningFlag: response?.warningFlag === true, loadError: false })}
            onError={() => this.setState({ loadError: true })}
            onAction={this.onAction.bind(this)}
            actions={this.actions}
          />
        </div>
      </Fragment>
    );
  }
}

const withCashbackV2Enabled = withFeatureFlag(FeatureFlags.CASHBACK_V2_ENABLED, "cashbackV2Enabled");

export default withRouter(withCashbackV2Enabled(CashbackConfigListView));
